/**
 * System Admin console (admin users only): manage Roles, Users, Access
 * (object/action grants), and Data. Surface-agnostic — every action goes
 * through the `ShellApi`, so the Run view, the site preview and the standalone
 * runtime render THIS screen rather than three that drift apart.
 *
 * Two rules shape it:
 *
 *   1. An administrator never sets another person's password. People are
 *      provisioned by invitation (they choose their own on accept) and helped
 *      back in with a reset link (they choose a new one). So there is no
 *      password field on this screen at all.
 *
 *   2. Roles and objects are PICKED, not typed. A grant naming a role nobody
 *      holds, or an object that does not exist, silently protects nothing and
 *      looks exactly like a grant that works.
 *
 *   3. Each object offers ITS OWN rights. A transaction has add / edit /
 *      delete and every action button on its form; a report has view and
 *      print; a document repository has the DMS's ten. The list comes from the
 *      server (`ShellObject.actions`), never from a constant here — so a
 *      button added to a form is grantable the moment the model is saved.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import {
  Check, ChevronDown, Loader2, Lock, LockOpen, Mail, Plus, Search, Smartphone, Trash2, KeyRound,
  ShieldCheck, Shield, UserPlus, UserX, UserCheck,
} from 'lucide-react'
import ShellModal, { shellInput, shellBtn } from './ShellModal'
import { extractError } from './AccountModal'
import type {
  ShellApi, ShellCapabilities, ShellGrant, ShellIdentity, ShellNotify,
  ShellObject, ShellObjectAction, ShellRepositoryTargets, ShellRestriction,
  ShellRole, ShellSecurityContext, ShellUser, SignInPolicy,
} from './types'

/** A surface-provided extra tab for the System administration console. */
export interface AdminExtraTab {
  id: string
  label: string
  render: () => ReactNode
}

export default function AdminConsole({
  open, onClose, identity, api, caps, extraTabs = [], notify,
}: {
  open: boolean
  onClose: () => void
  identity: ShellIdentity
  api: ShellApi
  caps: ShellCapabilities
  extraTabs?: AdminExtraTab[]
  /** Host toast adapter; without one, successes stay silent (the lists reload). */
  notify?: ShellNotify
}) {
  // Roles are what the other two tabs pick from, so defining one is the step
  // that comes before assigning or granting it — hence first, and the default.
  const showRoles = (caps.roles ?? caps.access) && !!api.listRoles
  const [tab, setTab] = useState<string>(showRoles ? 'roles' : 'users')
  const tabs: { id: string; label: string; show: boolean }[] = [
    { id: 'roles', label: 'Roles', show: showRoles },
    { id: 'users', label: 'Users', show: caps.users },
    { id: 'access', label: 'Access', show: caps.access },
    { id: 'data', label: 'Data', show: true },
    { id: 'signin', label: 'Sign\u2011in', show: !!api.getSignInPolicy },
    ...extraTabs.map((t) => ({ id: t.id, label: t.label, show: true })),
  ]
  const extra = extraTabs.find((t) => t.id === tab)

  // The role catalogue is loaded once here and shared by the Users and Access
  // tabs, so switching tabs doesn't refetch and the two can never disagree
  // about which roles exist.
  const [roles, setRoles] = useState<ShellRole[] | null>(null)
  // Two errors, deliberately not one. `rolesErr` means the catalogue could not
  // be LOADED — there is nothing to draw, so it replaces the tab. `rolesActionErr`
  // means one action was refused; the catalogue is still there and must stay on
  // screen, or a server that says "no" turns the whole tab into a single line of
  // red text with no way back but closing the panel.
  const [rolesErr, setRolesErr] = useState<string | null>(null)
  const [rolesActionErr, setRolesActionErr] = useState<string | null>(null)
  const reloadRoles = useCallback(() => {
    if (!api.listRoles) { setRoles([]); return }
    setRolesErr(null)
    api.listRoles()
      .then((r) => { setRoles(r); setRolesActionErr(null) })
      .catch((e) => setRolesErr(extractError(e) || 'Failed to load roles'))
  }, [api])
  useEffect(() => { if (open) reloadRoles() }, [open, reloadRoles])

  return (
    <ShellModal open={open} onClose={onClose} title="System administration" width={760}>
      <div style={{ display: 'flex', gap: 6, marginBottom: 14, borderBottom: '1px solid #e2e8f0' }}>
        {tabs.filter((t) => t.show).map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)}
            style={{
              padding: '0.5rem 0.85rem', background: 'none', border: 'none', cursor: 'pointer',
              fontWeight: tab === t.id ? 700 : 500, fontSize: '0.9375rem',
              color: tab === t.id ? '#4f46e5' : '#64748b',
              borderBottom: tab === t.id ? '2px solid #4f46e5' : '2px solid transparent', marginBottom: -1,
            }}>
            {t.label}
          </button>
        ))}
      </div>
      {tab === 'roles' && (
        <RolesTab roles={roles} err={rolesErr} actionErr={rolesActionErr} api={api}
          notify={notify} onChanged={reloadRoles} onError={setRolesActionErr} />
      )}
      {tab === 'users' && (
        <UsersTab identity={identity} api={api} roles={roles || []} notify={notify} />
      )}
      {tab === 'access' && <AccessTab api={api} roles={roles || []} />}
      {tab === 'data' && <DataTab caps={caps} />}
      {tab === 'signin' && <SignInTab api={api} notify={notify} />}
      {extra && extra.render()}
    </ShellModal>
  )
}

// ── Roles ──────────────────────────────────────────────────────────
function RolesTab({ roles, err, actionErr, api, notify, onChanged, onError }: {
  roles: ShellRole[] | null
  /** The catalogue could not be loaded — nothing to draw. */
  err: string | null
  /** One action was refused — the catalogue is still valid and stays on screen. */
  actionErr?: string | null
  api: ShellApi
  notify?: ShellNotify
  onChanged: () => void
  onError: (m: string) => void
}) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [busy, setBusy] = useState(false)

  const add = async () => {
    if (!name.trim()) { onError('A role name is required.'); return }
    setBusy(true)
    try {
      await api.createRole?.(name.trim(), description.trim())
      setName(''); setDescription('')
      notify?.success('Role added')
      onChanged()
    } catch (e) { onError(extractError(e) || 'Could not add the role') } finally { setBusy(false) }
  }

  const describe = async (r: ShellRole) => {
    const text = window.prompt(`Description for "${r.name}":`, r.description)
    if (text === null) return
    setBusy(true)
    try { await api.updateRole?.(r.name, text); onChanged() }
    catch (e) { onError(extractError(e) || 'Could not update the role') }
    finally { setBusy(false) }
  }

  const remove = async (r: ShellRole) => {
    // Say what it COSTS, not just "are you sure?". Deleting a role also drops
    // its grants — leaving them would keep objects locked to a role nobody can
    // hold, which is an outage nobody connects back to this click.
    const parts: string[] = []
    if (r.user_count) parts.push(`${r.user_count} user${r.user_count === 1 ? '' : 's'} will lose it`)
    if (r.object_count) parts.push(`${r.object_count} object grant${r.object_count === 1 ? '' : 's'} will be removed`)
    const detail = parts.length ? `\n\n${parts.join(', and ')}.` : ''
    if (!window.confirm(`Delete the role "${r.name}"?${detail}`)) return
    setBusy(true)
    try { await api.deleteRole?.(r.name); onChanged() }
    catch (e) { onError(extractError(e) || 'Could not delete the role') }
    finally { setBusy(false) }
  }

  if (err) return <ErrorLine text={err} />
  if (!roles) return <Spinner />

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {actionErr && <ErrorLine text={actionErr} />}
      <p style={{ margin: 0, fontSize: '0.875rem', color: '#64748b', lineHeight: 1.5 }}>
        Define the roles this application uses, then assign them to users and grant them
        actions on objects. Three are reserved: <code>admin</code> (full access, implied by
        administrator accounts), <code>func-admin</code> (approves any workflow step) and{' '}
        <code>site-visitor</code> (what an unauthenticated visitor counts as on public pages).
      </p>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <input style={{ ...shellInput, flex: '1 1 140px' }} value={name}
          placeholder="e.g. warehouse_manager"
          onChange={(e) => setName(e.target.value)} />
        <input style={{ ...shellInput, flex: '2 1 200px' }} value={description}
          placeholder="What this role is for (optional)"
          onChange={(e) => setDescription(e.target.value)} />
        <button type="button" style={{ ...shellBtn('primary'), opacity: busy ? 0.6 : 1 }}
          disabled={busy} onClick={add}>Add role</button>
      </div>

      <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
        {roles.map((r) => (
          <div key={r.name} style={{
            padding: '0.6rem 0.75rem', borderBottom: '1px solid #f1f5f9',
            display: 'flex', flexDirection: 'column', gap: 4,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <code style={{ fontSize: '0.875rem', fontWeight: 600 }}>{r.name}</code>
              {r.builtin && <span style={{ fontSize: '0.8125rem', color: '#94a3b8' }}>reserved</span>}
              <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                <IconBtn title="Describe" disabled={busy} onClick={() => describe(r)}>✎</IconBtn>
                <IconBtn title={r.builtin ? 'Reserved roles cannot be deleted' : 'Delete role'}
                  disabled={busy || r.builtin} danger onClick={() => remove(r)}>
                  <Trash2 size={15} />
                </IconBtn>
              </span>
            </div>
            <div style={{ fontSize: '0.8125rem', color: '#94a3b8' }}>
              {r.description || 'No description'} · {r.user_count} user{r.user_count === 1 ? '' : 's'},{' '}
              {r.object_count} object{r.object_count === 1 ? '' : 's'}
            </div>
          </div>
        ))}
        {roles.length === 0 && (
          <div style={{ padding: '0.75rem', fontSize: '0.875rem', color: '#94a3b8' }}>
            No roles defined yet — add one above.
          </div>
        )}
      </div>
    </div>
  )
}

/** Checkbox list of the DEFINED roles. Replaces the comma-separated text box:
 *  a role that is picked always matches the role a grant names. */
function RolePicker({ roles, selected, onChange, disabled, exclude }: {
  roles: ShellRole[]; selected: string[]; onChange: (next: string[]) => void; disabled?: boolean
  /** Role names not offered here (e.g. the administrator tier inside one
   *  entity — administering the app is never true in one entity only). */
  exclude?: string[]
}) {
  // admin follows the account flag
  const assignable = roles.filter((r) => r.name !== 'admin' && !(exclude || []).includes(r.name))
  const toggle = (name: string) =>
    onChange(selected.includes(name) ? selected.filter((r) => r !== name) : [...selected, name])
  if (assignable.length === 0) {
    return (
      <span style={{ fontSize: '0.8125rem', color: '#94a3b8' }}>
        No roles defined yet — add one on the Roles tab.
      </span>
    )
  }
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px' }}>
      {assignable.map((r) => (
        <label key={r.name} title={r.description || undefined}
          style={{
            display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.875rem',
            opacity: disabled ? 0.6 : 1,
          }}>
          <input type="checkbox" disabled={disabled} checked={selected.includes(r.name)}
            onChange={() => toggle(r.name)} />
          {r.name}
        </label>
      ))}
    </div>
  )
}

// ── Users ──────────────────────────────────────────────────────────
function UsersTab({ identity, api, roles, notify }: {
  identity: ShellIdentity; api: ShellApi; roles: ShellRole[]; notify?: ShellNotify
}) {
  const [users, setUsers] = useState<ShellUser[] | null>(null)
  // A LOAD failure leaves nothing to draw; a refused ACTION does not. Sharing
  // one state for both meant a server that answered "cannot delete the last
  // admin" replaced the entire user list with that one sentence, and the tab
  // had to be closed and reopened to get it back — which reads as the delete
  // button having broken the screen rather than as one request being declined.
  const [err, setErr] = useState<string | null>(null)
  const [actionErr, setActionErr] = useState<string | null>(null)
  const [inviting, setInviting] = useState(false)
  // Context-based security: when the app declares one (e.g. Financial
  // Entity), roles can also be given inside ONE of its values.
  const [secCtx, setSecCtx] = useState<ShellSecurityContext | null>(null)
  useEffect(() => {
    let cancelled = false
    api.securityContext?.()
      .then((sc) => { if (!cancelled) setSecCtx(sc) })
      .catch(() => { /* the global role editor still works */ })
    return () => { cancelled = true }
  }, [api])

  const reload = useCallback(() => {
    setErr(null)
    api.listUsers?.()
      .then((u) => { setUsers(u); setActionErr(null) })
      .catch((e) => setErr(extractError(e) || 'Failed to load users'))
  }, [api])
  useEffect(() => { reload() }, [reload])

  if (err) return <ErrorLine text={err} />
  if (!users) return <Spinner />

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {actionErr && <ErrorLine text={actionErr} />}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
        <div style={{ fontSize: '0.875rem', color: '#64748b' }}>{users.length} user(s)</div>
        {api.inviteUser && (
          <button type="button" style={shellBtn('primary')} onClick={() => setInviting(true)}>
            <UserPlus size={14} style={{ verticalAlign: -2, marginRight: 4 }} /> Invite user
          </button>
        )}
      </div>
      <p style={{ margin: 0, fontSize: '0.8125rem', color: '#94a3b8', lineHeight: 1.5 }}>
        New users are invited by email and choose their own password — nobody else
        ever sets it. The <code>admin</code> role is implied by administrator accounts,
        so it isn't in the role list.
      </p>
      {secCtx && (
        <p style={{ margin: 0, fontSize: '0.8125rem', color: '#64748b', lineHeight: 1.5 }}>
          Rights follow the <b>{secCtx.label}</b> a user signs into. Roles under
          “In every {secCtx.label}” apply everywhere; roles given inside one{' '}
          {secCtx.label} apply only while the user is signed into it. A user may
          only pick a {secCtx.label} they hold a role in.
          {!secCtx.supported && (
            <span style={{ color: '#b45309' }}>
              {' '}Roles per {secCtx.label} are not available on this database yet.
            </span>
          )}
        </p>
      )}
      <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
        {users.map((u) => (
          <UserRow key={u.username} u={u} roles={roles} self={u.username === identity.username}
            api={api} notify={notify} onChanged={reload} onError={setActionErr}
            securityContext={secCtx} />
        ))}
        {users.length === 0 && (
          <div style={{ padding: '0.75rem', fontSize: '0.875rem', color: '#94a3b8' }}>
            No users yet — invite your first one.
          </div>
        )}
      </div>
      {inviting && (
        <InviteUserForm api={api} roles={roles} notify={notify}
          onDone={() => { setInviting(false); reload() }} onCancel={() => setInviting(false)} />
      )}
    </div>
  )
}

function UserRow({ u, roles: catalogue, self, api, notify, onChanged, onError, securityContext }: {
  u: ShellUser; roles: ShellRole[]; self: boolean; api: ShellApi; notify?: ShellNotify
  onChanged: () => void; onError: (m: string) => void
  securityContext?: ShellSecurityContext | null
}) {
  // Show the non-implicit roles (drop the derived 'admin' for editing).
  const editable = useMemo(
    () => u.roles.filter((r) => !(u.is_admin && r === 'admin')), [u.roles, u.is_admin])
  const [selected, setSelected] = useState<string[]>(editable)
  const [editing, setEditing] = useState(false)
  const [busy, setBusy] = useState(false)
  const active = u.is_active !== false

  const run = async (fn: () => Promise<unknown>, confirmMsg?: string) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return
    setBusy(true)
    try { await fn(); onChanged() } catch (e) { onError(extractError(e) || 'Action failed') } finally { setBusy(false) }
  }

  const sendReset = () => run(async () => {
    const res = await api.sendPasswordReset?.(u.username)
    notify?.success(
      res && res.email_sent === false
        ? 'Reset link created, but the email could not be sent'
        : 'Reset link sent',
    )
  }, `Email ${u.email || u.username} a link to choose a new password?`)

  return (
    <div style={{
      padding: '0.6rem 0.75rem', borderBottom: '1px solid #f1f5f9',
      display: 'flex', flexDirection: 'column', gap: 6, opacity: active ? 1 : 0.65,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 600, fontSize: '0.9375rem' }}>{u.username}</span>
        {u.is_admin && <Badge>admin</Badge>}
        {!active && <Badge tone="muted">disabled</Badge>}
        {u.locked && <Badge tone="muted">locked</Badge>}
        {u.mfa_enabled && <Badge tone="muted">2FA</Badge>}
        {self && <span style={{ fontSize: '0.8125rem', color: '#94a3b8' }}>(you)</span>}
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          {busy && <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />}
          {api.setUserAdmin && (
            <IconBtn title={u.is_admin ? 'Revoke admin' : 'Make admin'} disabled={busy || self}
              onClick={() => run(
                async () => { await api.setUserAdmin?.(u.username, !u.is_admin) },
                u.is_admin ? `Revoke admin from ${u.username}?` : `Grant admin to ${u.username}?`)}>
              {u.is_admin ? <Shield size={15} /> : <ShieldCheck size={15} />}
            </IconBtn>
          )}
          {u.locked && api.unlockUser && (
            <IconBtn title="Unlock — locked after too many failed sign-ins" disabled={busy}
              onClick={() => run(async () => {
                await api.unlockUser?.(u.username)
                notify?.success(`${u.username} can sign in again`)
              })}>
              <LockOpen size={15} />
            </IconBtn>
          )}
          {u.mfa_enabled && api.resetUserMfa && (
            <IconBtn title="Reset two-factor sign-in (lost phone) — they set it up again at next sign-in"
              disabled={busy || self}
              onClick={() => run(async () => {
                await api.resetUserMfa?.(u.username)
                notify?.success(`Two-factor sign-in reset for ${u.username}`)
              }, `Reset two-factor sign-in for ${u.username}? Their authenticator and recovery codes stop working and they are signed out.`)}>
              <Smartphone size={15} />
            </IconBtn>
          )}
          {api.sendPasswordReset && (
            <IconBtn title="Email a password-reset link" disabled={busy} onClick={sendReset}>
              <KeyRound size={15} />
            </IconBtn>
          )}
          {api.setUserActive && (
            <IconBtn title={active ? 'Disable this account' : 'Enable this account'}
              disabled={busy || (self && active)}
              onClick={() => run(
                async () => { await api.setUserActive?.(u.username, !active) },
                active
                  ? `Disable ${u.username}? They keep their records and roles, and you can switch them back on.`
                  : undefined)}>
              {active ? <UserX size={15} /> : <UserCheck size={15} />}
            </IconBtn>
          )}
          <IconBtn title="Edit roles" disabled={busy} onClick={() => setEditing((v) => !v)}>✎</IconBtn>
          {api.deleteUser && (
            <IconBtn title="Delete user" disabled={busy || self} danger
              onClick={() => run(
                async () => { await api.deleteUser?.(u.username) },
                `Delete user "${u.username}"? This cannot be undone — to remove access reversibly, disable them instead.`)}>
              <Trash2 size={15} />
            </IconBtn>
          )}
        </span>
      </div>
      <div style={{ fontSize: '0.8125rem', color: '#94a3b8' }}>
        {u.email || 'no email'} · {securityContext ? `in every ${securityContext.label}: ` : 'roles: '}
        {editable.join(', ') || (securityContext ? '—' : '— (no access)')}
        {securityContext && Object.entries(u.context_roles || {})
          .filter(([, rs]) => rs.length > 0)
          .map(([cv, rs]) => (
            <span key={cv}> · <b style={{ color: '#64748b' }}>
              {contextValueLabel(securityContext, cv)}</b>: {rs.join(', ')}</span>
          ))}
      </div>
      {editing && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 2 }}>
          {securityContext && (
            <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#475569' }}>
              In every {securityContext.label}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <div style={{ flex: 1 }}>
              <RolePicker roles={catalogue} selected={selected} onChange={setSelected} disabled={busy} />
            </div>
            <button type="button" style={shellBtn('primary')} disabled={busy}
              onClick={() => run(async () => {
                await api.setRoles?.(u.username, selected)
                if (!securityContext) setEditing(false)
              })}>Save</button>
          </div>
          {securityContext && securityContext.supported && api.setContextRoles && (
            <ContextRolesEditor u={u} catalogue={catalogue} sc={securityContext}
              busy={busy} run={run} api={api} />
          )}
        </div>
      )}
    </div>
  )
}

/** The option label for a context value, falling back to the value itself. */
function contextValueLabel(sc: ShellSecurityContext, value: string): string {
  const o = sc.options.find((x) => String(x.value) === String(value))
  return o?.label && o.label !== value ? `${o.label} (${value})` : value
}

/** The administrator tier — never held inside one context value only. */
const ADMIN_TIER = ['admin', 'func-admin']

/** Roles a user holds inside ONE value of the security context each — one row
 *  per value they hold something in, plus "Add <label>" for another. */
function ContextRolesEditor({ u, catalogue, sc, busy, run, api }: {
  u: ShellUser; catalogue: ShellRole[]; sc: ShellSecurityContext; busy: boolean
  run: (fn: () => Promise<unknown>, confirmMsg?: string) => Promise<void>; api: ShellApi
}) {
  const held = useMemo(
    () => Object.entries(u.context_roles || {}).filter(([, rs]) => rs.length > 0),
    [u.context_roles])
  // Values added in this editor but not saved yet.
  const [added, setAdded] = useState<string[]>([])
  const [drafts, setDrafts] = useState<Record<string, string[]>>(
    () => Object.fromEntries(held.map(([cv, rs]) => [cv, rs])))
  // After a save the list reloads: take what the server now holds, keeping
  // only the drafts of values added here and not saved yet.
  useEffect(() => {
    setDrafts((d) => ({
      ...Object.fromEntries(added.map((cv) => [cv, d[cv] || []])),
      ...Object.fromEntries(held.map(([cv, rs]) => [cv, rs])),
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [held])
  const rows = [...held.map(([cv]) => cv), ...added.filter((cv) => !held.some(([h]) => h === cv))]
  const free = sc.options.filter((o) => !rows.includes(String(o.value)))

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6,
      borderTop: '1px dashed #e2e8f0', paddingTop: 8 }}>
      <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#475569' }}>
        In one {sc.label} only
      </div>
      {rows.length === 0 && (
        <div style={{ fontSize: '0.8125rem', color: '#94a3b8' }}>
          No {sc.label}-specific roles.
        </div>
      )}
      {rows.map((cv) => (
        <div key={cv} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <div style={{ width: 150, fontSize: '0.875rem', fontWeight: 600, paddingTop: 2,
            overflow: 'hidden', textOverflow: 'ellipsis' }} title={cv}>
            {contextValueLabel(sc, cv)}
          </div>
          <div style={{ flex: 1 }}>
            <RolePicker roles={catalogue} selected={drafts[cv] || []} disabled={busy}
              exclude={ADMIN_TIER}
              onChange={(next) => setDrafts((d) => ({ ...d, [cv]: next }))} />
          </div>
          <button type="button" style={shellBtn('primary')} disabled={busy}
            onClick={() => run(async () => {
              await api.setContextRoles?.(u.username, cv, drafts[cv] || [])
              setAdded((a) => a.filter((x) => x !== cv))
            })}>Save</button>
          <button type="button" style={shellBtn('ghost')} disabled={busy}
            title={`Remove every role ${u.username} holds in ${cv}`}
            onClick={() => {
              if (!held.some(([h]) => h === cv)) {
                setAdded((a) => a.filter((x) => x !== cv)); return
              }
              void run(async () => { await api.setContextRoles?.(u.username, cv, []) },
                `Remove ${u.username}'s roles in ${contextValueLabel(sc, cv)}?`)
            }}><Trash2 size={13} /></button>
        </div>
      ))}
      {!sc.source_ok ? (
        <div style={{ fontSize: '0.8125rem', color: '#b45309' }}>
          The list of {sc.label} values cannot be read right now.
        </div>
      ) : free.length > 0 ? (
        <select style={{ ...shellInput, maxWidth: 260 }} value="" disabled={busy}
          onChange={(e) => {
            const v = e.target.value
            if (!v) return
            setAdded((a) => [...a, v])
            setDrafts((d) => ({ ...d, [v]: d[v] || [] }))
          }}>
          <option value="">+ Add a {sc.label}…</option>
          {free.map((o) => (
            <option key={String(o.value)} value={String(o.value)}>{o.label || o.value}</option>
          ))}
        </select>
      ) : null}
    </div>
  )
}

function InviteUserForm({ api, roles, notify, onDone, onCancel }: {
  api: ShellApi; roles: ShellRole[]; notify?: ShellNotify; onDone: () => void; onCancel: () => void
}) {
  const [email, setEmail] = useState('')
  const [selected, setSelected] = useState<string[]>([])
  const [isAdmin, setIsAdmin] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErr(null); setBusy(true)
    try {
      const res = await api.inviteUser?.({ email: email.trim(), roles: selected, is_admin: isAdmin })
      if (res && res.mode === 'added_existing') notify?.success('Existing user added to this app')
      else if (res && res.email_sent === false) notify?.success('Invitation created, but the email could not be sent')
      else notify?.success('Invitation sent')
      onDone()
    } catch (e2) { setErr(extractError(e2) || 'Could not send the invitation') } finally { setBusy(false) }
  }

  return (
    <form onSubmit={submit} style={{
      border: '1px solid #e2e8f0', borderRadius: 10, padding: 12,
      display: 'flex', flexDirection: 'column', gap: 10, background: '#f8fafc',
    }}>
      <div style={{ fontWeight: 700, fontSize: '0.9375rem', display: 'flex', alignItems: 'center', gap: 6 }}>
        <Mail size={15} /> Invite a user
      </div>
      <input style={shellInput} type="email" placeholder="user@example.com" value={email}
        onChange={(e) => setEmail(e.target.value)} autoFocus />
      <div>
        <div style={{ fontSize: '0.8125rem', color: '#64748b', marginBottom: 4 }}>Roles</div>
        <RolePicker roles={roles} selected={selected} onChange={setSelected} />
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.875rem', color: '#475569' }}>
        <input type="checkbox" checked={isAdmin} onChange={(e) => setIsAdmin(e.target.checked)} /> Administrator
      </label>
      <div style={{ fontSize: '0.8125rem', color: '#94a3b8', lineHeight: 1.5 }}>
        They receive a link and choose their own password. If the address already
        has an account here, they are added to this application directly.
      </div>
      {err && <ErrorLine text={err} />}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button type="button" style={shellBtn('ghost')} onClick={onCancel}>Cancel</button>
        <button type="submit" style={{ ...shellBtn('primary'), opacity: busy || !email.trim() ? 0.6 : 1 }}
          disabled={busy || !email.trim()}>{busy ? 'Sending…' : 'Send invitation'}</button>
      </div>
    </form>
  )
}

// ── Access (grants) ────────────────────────────────────────────────
//
// Role-first: pick a role, see everything it may do as one list, add an
// object to that list or take one off it. Each object offers ITS OWN rights —
// a transaction its add / edit / delete plus every action button on the form,
// a report view / print, a document repository the DMS's ten — so nobody is
// asked whether a dashboard may be "deleted". Every tick saves at once.

/** What to CALL an object on this screen: its menu title, with the obj_name
 *  kept alongside rather than replaced. The title is what an administrator
 *  recognises; the obj_name stays because it is what the grant stores. */
function objectTitle(o: ShellObject | undefined, objName: string): string {
  const title = (o?.title || '').trim()
  return title && title !== objName ? title : objName
}

/** A surface that predates per-object actions (an older runtime server)
 *  sends none; the transaction vocabulary is the safe stand-in. */
const FALLBACK_ACTIONS: ShellObjectAction[] = [
  { name: 'view', label: 'View', help: 'See the list and open records', kind: 'base' },
  { name: 'add', label: 'Add', help: 'Create new records', kind: 'base' },
  { name: 'edit', label: 'Edit', help: 'Change existing records', kind: 'base' },
  { name: 'delete', label: 'Delete', help: 'Remove records', kind: 'base' },
]

/** Older grants were written in the endpoint vocabulary (`list`, `load`,
 *  `save`, …) or the generator's (`create`, `update`, …). Each such word is
 *  read as the plain right it is a synonym for — but only when the object
 *  does not offer the word itself (a process offers `save` outright). */
const SYNONYMS: Record<string, string[]> = {
  view: ['list', 'load', 'read', 'browse'],
  add: ['create', 'new'],
  edit: ['save', 'item_change', 'validate', 'update', 'modify'],
  delete: ['remove'],
}

const TYPE_COLORS: Record<string, { bg: string; fg: string }> = {
  T: { bg: '#eef2ff', fg: '#4338ca' },
  V: { bg: '#ecfeff', fg: '#0e7490' },
  D: { bg: '#f0fdf4', fg: '#15803d' },
  R: { bg: '#fff7ed', fg: '#c2410c' },
  G: { bg: '#fdf4ff', fg: '#a21caf' },
  S: { bg: '#f8fafc', fg: '#475569' },
  K: { bg: '#fefce8', fg: '#a16207' },
}

function TypeBadge({ o }: { o: ShellObject | undefined }) {
  const letter = (o?.obj_type || '').toUpperCase()
  const label = o?.type_label || ({
    T: 'Transaction', V: 'Visual', D: 'Dashboard', R: 'Report',
    G: 'Process', S: 'Page', K: 'Document repository',
  } as Record<string, string>)[letter] || (letter ? `Type ${letter}` : 'Object')
  const c = TYPE_COLORS[letter] || TYPE_COLORS.S
  return (
    <span style={{
      fontSize: '0.8125rem', fontWeight: 600, color: c.fg, background: c.bg,
      padding: '1px 8px', borderRadius: 999, whiteSpace: 'nowrap',
    }}>{label}</span>
  )
}

/** A grant's words folded onto the object's vocabulary. */
function grantState(acts: string[], vocabulary: ShellObjectAction[]) {
  const words = new Set(acts.map((a) => (a || '').trim()).filter(Boolean))
  const listed = new Set(vocabulary.map((a) => a.name))
  const full = words.has('*')
  const absorbed = new Set<string>()
  const isOn = (name: string) => {
    if (full || words.has(name)) return true
    return (SYNONYMS[name] || []).some((w) => !listed.has(w) && words.has(w))
  }
  for (const a of vocabulary) {
    for (const w of SYNONYMS[a.name] || []) if (!listed.has(w) && words.has(w)) absorbed.add(w)
  }
  // Words the object does not offer and nothing above explains — kept
  // visible so a grant never silently carries a right the screen hides.
  const leftovers = [...words].filter((w) => w !== '*' && !listed.has(w) && !absorbed.has(w))
  return { full, isOn, leftovers, words }
}

function nextActions(acts: string[], vocabulary: ShellObjectAction[], name: string, on: boolean): string[] {
  const listed = new Set(vocabulary.map((a) => a.name))
  const drop = new Set([name, ...(SYNONYMS[name] || []).filter((w) => !listed.has(w))])
  const kept = acts.filter((a) => !drop.has(a) && a !== '*')
  return on ? [...kept, name] : kept
}

function AccessTab({ api, roles }: { api: ShellApi; roles: ShellRole[] }) {
  const [grants, setGrants] = useState<ShellGrant[] | null>(null)
  const [objects, setObjects] = useState<ShellObject[]>([])
  const [err, setErr] = useState<string | null>(null)
  const [role, setRole] = useState('')
  const [pickingRole, setPickingRole] = useState(false)
  const [pickingObject, setPickingObject] = useState(false)
  const [saving, setSaving] = useState<string | null>(null)
  const [saved, setSaved] = useState<string | null>(null)

  const reload = useCallback(() => {
    setErr(null)
    api.listGrants?.().then(setGrants).catch((e) => setErr(extractError(e) || 'Failed to load rights'))
  }, [api])
  useEffect(() => { reload() }, [reload])
  useEffect(() => {
    // A surface without an object list still works — the picker then offers
    // the objects that already carry a grant.
    api.listObjects?.().then(setObjects).catch(() => setObjects([]))
  }, [api])

  // Nothing chosen yet: the first defined non-reserved role is the likeliest
  // thing an administrator came here to look at.
  useEffect(() => {
    if (!role && roles.length) setRole((roles.find((r) => !r.builtin) || roles[0]).name)
  }, [roles, role])

  const objectByName = useMemo(() => {
    const m = new Map<string, ShellObject>()
    for (const o of objects) m.set(o.obj_name, o)
    return m
  }, [objects])
  const roleGrants = useMemo(
    () => (grants || []).filter((g) => g.role === role)
      .sort((a, b) => objectTitle(objectByName.get(a.obj_name), a.obj_name)
        .localeCompare(objectTitle(objectByName.get(b.obj_name), b.obj_name))),
    [grants, role, objectByName])
  /** Every button any role has been granted BY NAME, per object — such a
   *  button is restricted to those roles, which the card says out loud. */
  const restrictedButtons = useMemo(() => {
    const m = new Map<string, Set<string>>()
    for (const g of grants || []) {
      for (const a of g.actions) {
        if (a.startsWith('action:') && a !== 'action:*') {
          if (!m.has(g.obj_name)) m.set(g.obj_name, new Set())
          m.get(g.obj_name)!.add(a)
        }
      }
    }
    return m
  }, [grants])
  const roleRec = roles.find((r) => r.name === role)

  const save = async (objName: string, actions: string[]) => {
    setSaving(objName); setErr(null)
    try {
      if (actions.length === 0) await api.deleteGrant?.(role, objName)
      else await api.setGrant?.(role, objName, actions)
      const fresh = await api.listGrants?.()
      if (fresh) setGrants(fresh)
      setSaved(objName)
      window.setTimeout(() => setSaved((cur) => (cur === objName ? null : cur)), 1600)
    } catch (e) {
      setErr(extractError(e) || 'Could not save the change')
      reload()
    } finally { setSaving(null) }
  }

  if (!grants && !err) return <Spinner />

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Who — the role whose rights the list below shows. */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 260px' }}>
          <div style={{ fontSize: '0.8125rem', color: '#64748b', marginBottom: 4 }}>Role</div>
          <button type="button" onClick={() => setPickingRole(true)}
            style={{
              ...shellInput, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
              textAlign: 'left', minHeight: 40,
            }}>
            {role ? (
              <>
                <span style={{ fontWeight: 600 }}>{role}</span>
                {roleRec?.description && (
                  <span style={{ color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {roleRec.description}
                  </span>
                )}
              </>
            ) : <span style={{ color: '#94a3b8' }}>Choose a role…</span>}
            <ChevronDown size={16} style={{ marginLeft: 'auto', color: '#64748b', flexShrink: 0 }} />
          </button>
        </div>
        {role && (
          <button type="button" style={shellBtn('primary')} onClick={() => setPickingObject(true)}
            disabled={!!saving}>
            <Plus size={14} style={{ verticalAlign: -2, marginRight: 4 }} /> Add object
          </button>
        )}
      </div>
      {roleRec && (
        <div style={{ fontSize: '0.8125rem', color: '#94a3b8', marginTop: -8 }}>
          {roleRec.user_count} user{roleRec.user_count === 1 ? '' : 's'} hold this role ·{' '}
          {roleGrants.length} object{roleGrants.length === 1 ? '' : 's'} granted
        </div>
      )}

      {err && <ErrorLine text={err} />}

      {!role ? (
        <div style={{ padding: '1.5rem', textAlign: 'center', fontSize: '0.875rem', color: '#94a3b8' }}>
          Choose a role to see what it may do.
        </div>
      ) : roleGrants.length === 0 ? (
        <div style={{
          border: '1px dashed #cbd5e1', borderRadius: 10, padding: '1.25rem',
          fontSize: '0.875rem', color: '#64748b', lineHeight: 1.6,
        }}>
          <strong style={{ color: '#0f172a' }}>{role}</strong> has no rights of its own yet.
          Access is deny-by-default: a role reaches an object only through a grant
          naming it, so this role can open nothing until you add one. Add an object to start.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {roleGrants.map((g) => (
            <ObjectRightsCard key={g.obj_name} grant={g} object={objectByName.get(g.obj_name)}
              restricted={restrictedButtons.get(g.obj_name)}
              busy={saving === g.obj_name} saved={saved === g.obj_name} disabled={!!saving}
              api={api} roles={roles}
              onChange={(actions) => save(g.obj_name, actions)}
              onRemove={() => save(g.obj_name, [])} />
          ))}
        </div>
      )}

      <p style={{ margin: 0, fontSize: '0.8125rem', color: '#94a3b8', lineHeight: 1.5 }}>
        <strong>Full access</strong> covers every right on the object, including buttons
        added later. A button ticked for one role becomes restricted to the roles that
        hold it; a button nobody has ticked is open to whoever may edit.
      </p>

      {pickingRole && (
        <RolePickerModal roles={roles} current={role}
          onPick={(name) => { setRole(name); setPickingRole(false) }}
          onClose={() => setPickingRole(false)} />
      )}
      {pickingObject && (
        <ObjectPickerModal objects={objects} taken={new Set(roleGrants.map((g) => g.obj_name))}
          role={role}
          onPick={(o) => { setPickingObject(false); save(o.obj_name, ['view']) }}
          onClose={() => setPickingObject(false)} />
      )}
    </div>
  )
}

/** One object in the role's list: its rights as tick boxes, a full-access
 *  switch, and the way off the list. */
function ObjectRightsCard({ grant, object, restricted, busy, saved, disabled, api, roles, onChange, onRemove }: {
  grant: ShellGrant
  object: ShellObject | undefined
  restricted: Set<string> | undefined
  busy: boolean
  saved: boolean
  disabled: boolean
  api: ShellApi
  roles: ShellRole[]
  onChange: (actions: string[]) => void
  onRemove: () => void
}) {
  const [restricting, setRestricting] = useState(false)
  const vocabulary = object?.actions?.length ? object.actions : FALLBACK_ACTIONS
  const base = vocabulary.filter((a) => a.kind !== 'button' && a.kind !== 'button_read')
  const buttons = vocabulary.filter((a) => a.kind === 'button' || a.kind === 'button_read')
  const state = grantState(grant.actions, vocabulary)
  const title = objectTitle(object, grant.obj_name)
  const isRepo = (object?.obj_type || '').toUpperCase() === 'K'

  // A plain render function, not a nested component: a component declared
  // inside render is a new type every render, which remounts each checkbox
  // (and drops its focus) on every save.
  const tick = (a: ShellObjectAction) => {
    const on = state.isOn(a.name)
    const lockedElsewhere = !on && !!restricted?.has(a.name)
    const hint = a.kind === 'button' || a.kind === 'button_read'
      ? (on ? (a.kind === 'button_read' ? 'Opens only, never writes' : 'Restricted to the roles that hold it')
        : lockedElsewhere ? 'Restricted to other roles' : 'Open to anyone who may edit')
      : a.help
    return (
      <label key={a.name} title={hint} style={{
        display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '0.875rem',
        padding: '3px 9px 3px 6px', borderRadius: 7, cursor: disabled ? 'default' : 'pointer',
        border: '1px solid', borderColor: on ? '#c7d2fe' : '#e2e8f0',
        background: on ? '#eef2ff' : '#fff', color: on ? '#3730a3' : '#334155',
        opacity: state.full ? 0.7 : 1,
      }}>
        <input type="checkbox" checked={on} disabled={disabled || state.full}
          onChange={(e) => onChange(nextActions(grant.actions, vocabulary, a.name, e.target.checked))}
          style={{ margin: 0 }} />
        {a.label}
        {lockedElsewhere && <Lock size={11} style={{ color: '#94a3b8' }} />}
      </label>
    )
  }

  return (
    <div style={{
      border: '1px solid #e2e8f0', borderRadius: 10, padding: '0.6rem 0.75rem',
      display: 'flex', flexDirection: 'column', gap: 8, opacity: busy ? 0.7 : 1,
      transition: 'opacity 120ms',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 600, fontSize: '0.9375rem' }}>{title}</span>
        {title !== grant.obj_name && (
          <code style={{ fontSize: '0.8125rem', color: '#94a3b8' }}>{grant.obj_name}</code>
        )}
        <TypeBadge o={object} />
        <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 10 }}>
          {busy ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite', color: '#64748b' }} />
            : saved ? <span style={{ fontSize: '0.8125rem', color: '#15803d', display: 'inline-flex', gap: 3, alignItems: 'center' }}><Check size={12} /> Saved</span>
              : null}
          <label title="Every right on this object, including buttons added later"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '0.8125rem', color: '#334155', cursor: 'pointer' }}>
            <input type="checkbox" checked={state.full} disabled={disabled}
              onChange={(e) => onChange(e.target.checked ? ['*'] : ['view'])} style={{ margin: 0 }} />
            Full access
          </label>
          <IconBtn title="Take this object off the list" danger disabled={disabled} onClick={onRemove}>
            <Trash2 size={14} />
          </IconBtn>
        </span>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {base.map(tick)}
      </div>
      {buttons.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
          <span style={{ fontSize: '0.8125rem', color: '#64748b', marginRight: 2 }}>Buttons</span>
          {buttons.map(tick)}
        </div>
      )}
      {state.leftovers.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
          <span style={{ fontSize: '0.8125rem', color: '#64748b', marginRight: 2 }}>Also granted</span>
          {state.leftovers.map((w) => (
            <span key={w} style={{
              fontSize: '0.8125rem', background: '#f1f5f9', color: '#475569', borderRadius: 999,
              padding: '2px 4px 2px 8px', display: 'inline-flex', gap: 4, alignItems: 'center',
            }}>
              <code>{w}</code>
              <button type="button" title={`Remove ${w}`} disabled={disabled}
                onClick={() => onChange(grant.actions.filter((x) => x !== w))}
                style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#94a3b8', padding: '0 3px', lineHeight: 1 }}>×</button>
            </span>
          ))}
        </div>
      )}
      {isRepo && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: '0.8125rem', color: '#64748b' }}>
          <span>Applies to the whole repository.</span>
          {object && api.listRepositoryRestrictions && (
            <button type="button" onClick={() => setRestricting(true)} disabled={disabled}
              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#4f46e5', fontWeight: 600, fontSize: '0.8125rem' }}>
              Restrict inside the repository…
            </button>
          )}
        </div>
      )}
      {restricting && object && (
        <RestrictionsModal repo={object} vocabulary={vocabulary} roles={roles} api={api}
          onClose={() => setRestricting(false)} />
      )}
    </div>
  )
}

// ── Repository restrictions ────────────────────────────────────────────
//
// The repository grant says what a role may do with the repository. These
// rows narrow that INSIDE it — for one business area, folder, kind of
// document or single document — the way row security narrows a transaction.
// A row can take a right away, or limit it to the places rows name; it can
// never give a right the grant does not.

const SCOPE_LABELS: Record<string, string> = {
  business_area: 'Business area',
  folder: 'Folder',
  doctype: 'Kind of document',
  document: 'One document',
}

function RestrictionsModal({ repo, vocabulary, roles, api, onClose }: {
  repo: ShellObject; vocabulary: ShellObjectAction[]; roles: ShellRole[]; api: ShellApi; onClose: () => void
}) {
  const [rows, setRows] = useState<ShellRestriction[] | null>(null)
  const [targets, setTargets] = useState<ShellRepositoryTargets>({ doctypes: [], folders: [] })
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [adding, setAdding] = useState(false)

  const reload = useCallback(() => {
    setErr(null)
    api.listRepositoryRestrictions?.(repo)
      .then((rs) => setRows(rs.filter((r) => r.scope !== 'repo')))
      .catch((e) => setErr(extractError(e) || 'Failed to load the restrictions'))
  }, [api, repo])
  useEffect(() => { reload() }, [reload])
  useEffect(() => {
    api.repositoryTargets?.(repo).then(setTargets).catch(() => setTargets({ doctypes: [], folders: [] }))
  }, [api, repo])

  const targetName = (r: ShellRestriction) => {
    if (r.scope === 'doctype') return targets.doctypes.find((t) => t.id === r.scope_id)?.title || r.scope_id
    if (r.scope === 'folder') return targets.folders.find((f) => f.id === r.scope_id)?.name || r.scope_id
    return r.scope_id
  }
  const rightsOn = (r: ShellRestriction) =>
    vocabulary.filter((a) => r.rights[a.name]).map((a) => a.label)

  const remove = async (r: ShellRestriction) => {
    setBusy(true); setErr(null)
    try {
      await api.saveRepositoryRestriction?.(repo, { ...r, rights: {} })
      reload()
    } catch (e) { setErr(extractError(e) || 'Could not remove the restriction') } finally { setBusy(false) }
  }

  return (
    <ShellModal open onClose={onClose} title={`Restrictions inside ${objectTitle(repo, repo.obj_name)}`} width={640}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <p style={{ margin: 0, fontSize: '0.875rem', color: '#64748b', lineHeight: 1.5 }}>
          A restriction narrows a role's rights inside one part of the repository. Ticked
          rights are what the role keeps there; once a role has a restriction that keeps a
          right, that right applies only in the places its restrictions name. A restriction
          never adds a right the role does not already have on the repository.
        </p>
        {err && <ErrorLine text={err} />}
        {!rows ? <Spinner /> : (
          <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
            {rows.map((r, i) => (
              <div key={`${r.scope}:${r.scope_id}:${r.principal_kind}:${r.principal_id}:${i}`} style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '0.5rem 0.75rem',
                borderBottom: '1px solid #f1f5f9', fontSize: '0.875rem', flexWrap: 'wrap',
              }}>
                <Badge>{r.principal_kind === 'user' ? `user ${r.principal_id}` : r.principal_id}</Badge>
                <span style={{ color: '#64748b' }}>{SCOPE_LABELS[r.scope] || r.scope}</span>
                <span style={{ fontWeight: 600 }}>{targetName(r)}</span>
                <span style={{ color: '#64748b' }}>
                  {rightsOn(r).length ? `keeps ${rightsOn(r).join(', ')}` : 'keeps nothing'}
                </span>
                <IconBtn title="Remove this restriction" danger disabled={busy} onClick={() => remove(r)}
                  style={{ marginLeft: 'auto' }}>
                  <Trash2 size={14} />
                </IconBtn>
              </div>
            ))}
            {rows.length === 0 && (
              <div style={{ padding: '0.75rem', fontSize: '0.875rem', color: '#94a3b8' }}>
                No restrictions — every role's repository rights apply everywhere in it.
              </div>
            )}
          </div>
        )}
        {adding ? (
          <RestrictionForm repo={repo} vocabulary={vocabulary} roles={roles} targets={targets} api={api}
            onDone={() => { setAdding(false); reload() }} onCancel={() => setAdding(false)} />
        ) : (
          <div>
            <button type="button" style={shellBtn('primary')} onClick={() => setAdding(true)} disabled={busy}>
              <Plus size={14} style={{ verticalAlign: -2, marginRight: 4 }} /> Add restriction
            </button>
          </div>
        )}
      </div>
    </ShellModal>
  )
}

function RestrictionForm({ repo, vocabulary, roles, targets, api, onDone, onCancel }: {
  repo: ShellObject; vocabulary: ShellObjectAction[]; roles: ShellRole[]
  targets: ShellRepositoryTargets; api: ShellApi; onDone: () => void; onCancel: () => void
}) {
  const [scope, setScope] = useState('doctype')
  const [scopeId, setScopeId] = useState('')
  const [kind, setKind] = useState('role')
  const [principal, setPrincipal] = useState('')
  const [rights, setRights] = useState<Record<string, boolean>>({ view: true })
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const picks = scope === 'doctype' ? targets.doctypes.map((t) => ({ id: t.id, label: t.title }))
    : scope === 'folder' ? targets.folders.map((f) => ({ id: f.id, label: f.path ? `${f.name}  (${f.path})` : f.name }))
      : null

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!scopeId.trim() || !principal.trim()) { setErr('Choose where it applies and who it is for.'); return }
    setBusy(true); setErr(null)
    try {
      await api.saveRepositoryRestriction?.(repo, {
        scope, scope_id: scopeId.trim(), principal_kind: kind, principal_id: principal.trim(), rights,
      })
      onDone()
    } catch (e2) { setErr(extractError(e2) || 'Could not save the restriction') } finally { setBusy(false) }
  }

  return (
    <form onSubmit={submit} style={{
      border: '1px solid #e2e8f0', borderRadius: 10, padding: 12, background: '#f8fafc',
      display: 'flex', flexDirection: 'column', gap: 10,
    }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.8125rem', color: '#64748b' }}>
          Where it applies
          <select style={shellInput} value={scope} onChange={(e) => { setScope(e.target.value); setScopeId('') }}>
            <option value="doctype">A kind of document</option>
            <option value="folder">A folder and everything beneath it</option>
            <option value="business_area">A business area</option>
            <option value="document">One document</option>
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.8125rem', color: '#64748b' }}>
          {scope === 'business_area' ? 'Business area value' : scope === 'document' ? 'Document id' : 'Which one'}
          {picks ? (
            <select style={shellInput} value={scopeId} onChange={(e) => setScopeId(e.target.value)}>
              <option value="">Choose…</option>
              {picks.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
          ) : (
            <input style={shellInput} value={scopeId} onChange={(e) => setScopeId(e.target.value)}
              placeholder={scope === 'business_area' ? 'e.g. manufacturing' : 'the document\u2019s id'} />
          )}
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.8125rem', color: '#64748b' }}>
          Who it is for
          <select style={shellInput} value={kind} onChange={(e) => { setKind(e.target.value); setPrincipal('') }}>
            <option value="role">A role</option>
            <option value="user">One person</option>
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.8125rem', color: '#64748b' }}>
          {kind === 'role' ? 'Role' : 'Username'}
          {kind === 'role' ? (
            <select style={shellInput} value={principal} onChange={(e) => setPrincipal(e.target.value)}>
              <option value="">Choose a role…</option>
              {roles.filter((r) => r.name !== 'admin').map((r) => <option key={r.name} value={r.name}>{r.name}</option>)}
            </select>
          ) : (
            <input style={shellInput} value={principal} onChange={(e) => setPrincipal(e.target.value)} placeholder="username" />
          )}
        </label>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <span style={{ fontSize: '0.8125rem', color: '#64748b' }}>Rights kept here (unticked rights are taken away in this place)</span>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {vocabulary.map((a) => (
            <label key={a.name} title={a.help} style={{
              display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '0.875rem',
              padding: '3px 9px 3px 6px', borderRadius: 7, cursor: 'pointer', border: '1px solid',
              borderColor: rights[a.name] ? '#c7d2fe' : '#e2e8f0',
              background: rights[a.name] ? '#eef2ff' : '#fff', color: rights[a.name] ? '#3730a3' : '#334155',
            }}>
              <input type="checkbox" checked={!!rights[a.name]} style={{ margin: 0 }}
                onChange={(e) => setRights((prev) => ({ ...prev, [a.name]: e.target.checked }))} />
              {a.label}
            </label>
          ))}
        </div>
      </div>
      {err && <ErrorLine text={err} />}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button type="button" style={shellBtn('ghost')} onClick={onCancel}>Cancel</button>
        <button type="submit" style={{ ...shellBtn('primary'), opacity: busy ? 0.6 : 1 }} disabled={busy}>
          {busy ? 'Saving…' : 'Save restriction'}
        </button>
      </div>
    </form>
  )
}

/** Search box + filter chips + a list, shared by both pickers. */
function PickerFrame({ title, placeholder, search, onSearch, chips, chip, onChip, children, onClose }: {
  title: string
  placeholder: string
  search: string
  onSearch: (v: string) => void
  chips: { id: string; label: string; count: number }[]
  chip: string
  onChip: (id: string) => void
  children: ReactNode
  onClose: () => void
}) {
  return (
    <ShellModal open onClose={onClose} title={title} width={560}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ position: 'relative' }}>
          <Search size={15} style={{ position: 'absolute', left: 10, top: 12, color: '#94a3b8' }} />
          <input autoFocus style={{ ...shellInput, paddingLeft: 32 }} value={search}
            placeholder={placeholder} onChange={(e) => onSearch(e.target.value)} />
        </div>
        {chips.length > 2 && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {chips.map((c) => (
              <button key={c.id} type="button" onClick={() => onChip(c.id)}
                style={{
                  padding: '0.2rem 0.6rem', borderRadius: 999, fontSize: '0.8125rem', cursor: 'pointer',
                  border: '1px solid', borderColor: chip === c.id ? '#4f46e5' : '#e2e8f0',
                  background: chip === c.id ? '#eef2ff' : '#fff', color: chip === c.id ? '#4f46e5' : '#475569',
                }}>
                {c.label} <span style={{ opacity: 0.6 }}>{c.count}</span>
              </button>
            ))}
          </div>
        )}
        <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'auto', maxHeight: '55vh' }}>
          {children}
        </div>
      </div>
    </ShellModal>
  )
}

function pickerRow(disabled: boolean): React.CSSProperties {
  return {
    display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left',
    padding: '0.55rem 0.75rem', border: 'none', borderBottom: '1px solid #f1f5f9',
    background: '#fff', cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.55 : 1,
    fontSize: '0.875rem', color: '#0f172a',
  }
}

function RolePickerModal({ roles, current, onPick, onClose }: {
  roles: ShellRole[]; current: string; onPick: (name: string) => void; onClose: () => void
}) {
  const [search, setSearch] = useState('')
  const [chip, setChip] = useState('all')
  const q = search.trim().toLowerCase()
  const matches = (r: ShellRole) =>
    (!q || r.name.includes(q) || (r.description || '').toLowerCase().includes(q))
    && (chip === 'all' || (chip === 'reserved') === r.builtin)
  const shown = roles.filter(matches)
  const chips = [
    { id: 'all', label: 'All', count: roles.length },
    { id: 'custom', label: 'Defined here', count: roles.filter((r) => !r.builtin).length },
    { id: 'reserved', label: 'Reserved', count: roles.filter((r) => r.builtin).length },
  ]
  return (
    <PickerFrame title="Choose a role" placeholder="Search roles…" search={search} onSearch={setSearch}
      chips={chips} chip={chip} onChip={setChip} onClose={onClose}>
      {shown.map((r) => (
        <button key={r.name} type="button" style={pickerRow(false)} onClick={() => onPick(r.name)}>
          <span style={{ fontWeight: 600 }}>{r.name}</span>
          {r.builtin && <span style={{ fontSize: '0.8125rem', color: '#94a3b8' }}>reserved</span>}
          {r.name === current && <Check size={14} style={{ color: '#4f46e5' }} />}
          <span style={{ marginLeft: 'auto', fontSize: '0.8125rem', color: '#94a3b8', whiteSpace: 'nowrap' }}>
            {r.user_count} user{r.user_count === 1 ? '' : 's'} · {r.object_count} object{r.object_count === 1 ? '' : 's'}
          </span>
          {r.description && (
            <span style={{ flexBasis: '100%', fontSize: '0.8125rem', color: '#64748b' }}>{r.description}</span>
          )}
        </button>
      ))}
      {shown.length === 0 && (
        <div style={{ padding: '0.75rem', fontSize: '0.875rem', color: '#94a3b8' }}>
          {roles.length === 0 ? 'No roles defined yet — add one on the Roles tab.' : 'No role matches.'}
        </div>
      )}
    </PickerFrame>
  )
}

function ObjectPickerModal({ objects, taken, role, onPick, onClose }: {
  objects: ShellObject[]; taken: Set<string>; role: string
  onPick: (o: ShellObject) => void; onClose: () => void
}) {
  const [search, setSearch] = useState('')
  const [chip, setChip] = useState('all')
  const [typed, setTyped] = useState('')
  const q = search.trim().toLowerCase()
  const types = useMemo(() => {
    const m = new Map<string, { label: string; count: number }>()
    for (const o of objects) {
      const t = (o.obj_type || '?').toUpperCase()
      const cur = m.get(t)
      m.set(t, { label: o.type_label || t, count: (cur?.count || 0) + 1 })
    }
    return [...m.entries()].sort((a, b) => a[1].label.localeCompare(b[1].label))
  }, [objects])
  const chips = [{ id: 'all', label: 'All', count: objects.length },
    ...types.map(([t, v]) => ({ id: t, label: v.label, count: v.count }))]
  const shown = objects.filter((o) =>
    (chip === 'all' || (o.obj_type || '?').toUpperCase() === chip)
    && (!q || o.obj_name.toLowerCase().includes(q) || (o.title || '').toLowerCase().includes(q)))
  return (
    <PickerFrame title={`Add an object for ${role}`} placeholder="Search by name or title…"
      search={search} onSearch={setSearch} chips={chips} chip={chip} onChip={setChip} onClose={onClose}>
      {shown.map((o) => {
        const has = taken.has(o.obj_name)
        const title = objectTitle(o, o.obj_name)
        return (
          <button key={o.obj_name} type="button" style={pickerRow(has)} disabled={has}
            onClick={() => onPick(o)} title={has ? 'Already in the list' : undefined}>
            <span style={{ fontWeight: 600 }}>{title}</span>
            {title !== o.obj_name && <code style={{ fontSize: '0.8125rem', color: '#94a3b8' }}>{o.obj_name}</code>}
            <span style={{ marginLeft: 'auto' }}><TypeBadge o={o} /></span>
            {has && <span style={{ fontSize: '0.8125rem', color: '#94a3b8' }}>added</span>}
          </button>
        )
      })}
      {shown.length === 0 && objects.length > 0 && (
        <div style={{ padding: '0.75rem', fontSize: '0.875rem', color: '#94a3b8' }}>No object matches.</div>
      )}
      {objects.length === 0 && (
        // No object list on this surface: the name can still be typed, and
        // is spelled out again beside the box so a slip is at least visible.
        <div style={{ padding: '0.75rem', display: 'flex', gap: 8, alignItems: 'center' }}>
          <input style={shellInput} placeholder="Object name (obj_name)" value={typed}
            onChange={(e) => setTyped(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && typed.trim()) onPick({ obj_name: typed.trim() }) }} />
          <button type="button" style={shellBtn('primary')} disabled={!typed.trim()}
            onClick={() => onPick({ obj_name: typed.trim() })}>Add</button>
        </div>
      )}
    </PickerFrame>
  )
}

// ── Data ───────────────────────────────────────────────────────────
function DataTab({ caps }: { caps: ShellCapabilities }) {
  if (caps.dataAdminUrl) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <p style={{ margin: 0, fontSize: '0.9375rem', color: '#475569' }}>
          Browse and manage the application's data records.
        </p>
        <a href={caps.dataAdminUrl} target="_blank" rel="noreferrer" style={{ ...shellBtn('primary'), textDecoration: 'none', alignSelf: 'flex-start' }}>
          Open data admin
        </a>
      </div>
    )
  }
  return (
    <div style={{ fontSize: '0.9375rem', color: '#64748b', lineHeight: 1.6 }}>
      A data browser for this surface is coming soon. For now, manage records from the builder's
      database tools.
    </div>
  )
}

// ── shared bits ────────────────────────────────────────────────────
function Spinner() {
  return <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}><Loader2 size={22} style={{ animation: 'spin 1s linear infinite' }} /></div>
}
function ErrorLine({ text }: { text: string }) {
  return <div style={{ color: '#dc2626', fontSize: '0.875rem' }}>{text}</div>
}
function Badge({ children, tone }: { children: React.ReactNode; tone?: 'muted' }) {
  const muted = tone === 'muted'
  return (
    <span style={{
      fontSize: '0.8125rem', fontWeight: 700,
      color: muted ? '#64748b' : '#4f46e5', background: muted ? '#f1f5f9' : '#eef2ff',
      padding: '1px 7px', borderRadius: 999,
    }}>{children}</span>
  )
}
function IconBtn({ children, onClick, title, disabled, danger, style }: {
  children: React.ReactNode; onClick: () => void; title: string; disabled?: boolean; danger?: boolean; style?: React.CSSProperties
}) {
  return (
    <button type="button" title={title} onClick={onClick} disabled={disabled}
      style={{
        background: 'none', border: '1px solid #e2e8f0', borderRadius: 6, width: 28, height: 26,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: disabled ? 'not-allowed' : 'pointer',
        color: danger ? '#dc2626' : '#475569', opacity: disabled ? 0.4 : 1, fontSize: 15, ...style,
      }}>{children}</button>
  )
}


const MFA_MODE_LABEL: Record<string, string> = {
  off: 'Off — not offered', optional: 'Optional — each person chooses', required: 'Required for everyone',
}

/** Admin → Sign-in: tighten two-factor sign-in for this organization. The
 *  server refuses anything looser than the application's own policy. */
function SignInTab({ api, notify }: { api: ShellApi; notify?: ShellNotify }) {
  const [pol, setPol] = useState<SignInPolicy | null>(null)
  const [mode, setMode] = useState('optional')
  const [grace, setGrace] = useState(7)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const adopt = (p: SignInPolicy) => {
    setPol(p)
    const cur = p.override || p.effective
    setMode(cur.mode); setGrace(cur.grace_days)
  }
  useEffect(() => {
    api.getSignInPolicy?.().then(adopt).catch((e) => setErr(extractError(e) || 'Could not load the sign-in policy'))
  }, [api])

  const save = async (mfa: { mode: string; grace_days: number } | null) => {
    setBusy(true); setErr(null)
    try {
      adopt(await api.setSignInPolicy!(mfa))
      notify?.success(mfa ? 'Sign-in policy saved' : 'Using the application’s setting')
    } catch (e) {
      setErr(extractError(e) || 'Could not save the policy')
    } finally { setBusy(false) }
  }

  if (!pol) return err ? <div style={{ color: '#dc2626', fontSize: '0.875rem' }}>{err}</div> : null
  if (!pol.supported) {
    return <p style={{ color: '#64748b', fontSize: '0.875rem' }}>Two-factor sign-in is not available for this application yet.</p>
  }
  const describe = (m: { mode: string; grace_days: number }) =>
    `${MFA_MODE_LABEL[m.mode] || m.mode}${m.mode === 'required' ? ` (${m.grace_days} days to set it up)` : ''}`

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 520, color: '#0f172a' }} data-tw-admin="signin">
      <div style={{ fontWeight: 700, fontSize: '0.9625rem' }}>Two-factor sign-in</div>
      <p style={{ margin: 0, color: '#64748b', fontSize: '0.875rem' }}>
        A 6-digit code from an authenticator app after the password. People set it up from
        My account; once it is required, anyone who has not is asked to before they can sign in
        (after the grace period). You can make it stricter for your organization, never looser
        than the application allows.
      </p>
      {pol.application && (
        <div style={{ fontSize: '0.875rem' }}><span style={{ color: '#64748b' }}>The application sets: </span>{describe(pol.application)}</div>
      )}
      <div style={{ fontSize: '0.875rem' }}><span style={{ color: '#64748b' }}>In force now: </span><strong>{describe(pol.effective)}</strong></div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.875rem', color: '#334155' }}>
          Your organization
          <select value={mode} onChange={(e) => setMode(e.target.value)} style={{ ...shellInput, width: 260 }}>
            {Object.entries(MFA_MODE_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
        {mode === 'required' && (
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.875rem', color: '#334155' }}>
            Grace period (days)
            <input type="number" min={0} max={90} value={grace} style={{ ...shellInput, width: 110 }}
              onChange={(e) => setGrace(Math.max(0, Math.min(90, Number.parseInt(e.target.value, 10) || 0)))} />
          </label>
        )}
      </div>
      {err && <div role="alert" style={{ color: '#dc2626', fontSize: '0.875rem' }}>{err}</div>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" style={shellBtn('primary')} disabled={busy}
          onClick={() => save({ mode, grace_days: grace })}>Save</button>
        {pol.override && (
          <button type="button" style={shellBtn('ghost')} disabled={busy} onClick={() => save(null)}>
            Use the application’s setting
          </button>
        )}
      </div>
    </div>
  )
}
